// src/components/gestionEvento/SessionManager.js

import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import AddSessionModal from './AddSessionModal';
import EditSessionModal from './EditSessionModal';
import SessionSchedules from './SessionSchedules';
import SchedulesPDF from './SchedulesPDF'; // Para descargar PDF de sesiones
import { useParams } from 'react-router-dom'; // Para obtener el slug de la URL
import { useCompetencia } from '../../context/CompetenciaContext';

function SessionManager() {
  // Obtenemos el slug desde la URL
  const { slug } = useParams();

  // Obtenemos el estado global y funciones del contexto
  const { competencias, addSession, updateSession, deleteSession } = useCompetencia();

  // Estados locales para el manejo de modales
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState(null);

  // Diálogo para confirmar borrado de sesión
  const [openConfirmDeleteSession, setOpenConfirmDeleteSession] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);

  // 1) Ubicamos la competencia por slug
  const competencia = useMemo(() => {
    return competencias.find((c) => c.slug === slug);
  }, [competencias, slug]);

  // 2) Si no existe, mostramos un mensaje
  if (!competencia) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          No existe la competencia con slug: {slug}
        </Typography>
      </Box>
    );
  }

  // 3) Obtenemos el array de sesiones
  const sessions = competencia.sessions || [];

  // 4) Agregar sesión
  const handleAddSession = (newSession) => {
    // Llamamos a addSession con el ID real de la competencia
    addSession(competencia.id, newSession);
    setOpenAddModal(false);
  };

  // 5) Actualizar sesión
  const handleUpdateSession = (updatedSession) => {
    // Llamamos a updateSession con el ID real de la competencia
    updateSession(competencia.id, updatedSession);
  };

  // 6) Manejo de modales
  const handleOpenAddModal = () => setOpenAddModal(true);
  const handleCloseAddModal = () => setOpenAddModal(false);

  // 7) Edición
  const handleEditSession = (session) => {
    setSessionToEdit(session);
    setOpenEditModal(true);
  };
  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSessionToEdit(null);
  };
  const handleSaveEditedSession = (updatedSession) => {
    updateSession(competencia.id, updatedSession);
    setOpenEditModal(false);
    setSessionToEdit(null);
  };

  // 8) Borrar sesión
  const handleDeleteSession = (session) => {
    setSessionToDelete(session);
    setOpenConfirmDeleteSession(true);
  };
  const handleConfirmDeleteSession = () => {
    if (!sessionToDelete) return;
    deleteSession(competencia.id, sessionToDelete.id);
    setSessionToDelete(null);
    setOpenConfirmDeleteSession(false);
  };

  // 9) Recolectamos todos los combinedEventId usados en todas las sesiones
  const allUsedIds = new Set();
  sessions.forEach((sess) => {
    (sess.schedules || []).forEach((schedule) => {
      allUsedIds.add(schedule.combinedEventId);
    });
  });

  console.log('allUsedIds', allUsedIds); // Agrega este console.log para verificar los IDs
  console.log('sessions', sessions); // Agrega este console.log para verificar las sesiones

  return (
    <Box sx={{ p: 2 }}>
      {/* Botón para abrir modal de agregar sesión */}
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleOpenAddModal}
        sx={{ mb: 2 }}
      >
        Agregar Sesión
      </Button>

      {/* Modal para crear sesión */}
      <AddSessionModal
        open={openAddModal}
        onClose={handleCloseAddModal}
        onAddSession={handleAddSession}
      />

      {/* Modal para editar sesión */}
      <EditSessionModal
        open={openEditModal}
        onClose={handleCloseEditModal}
        sessionToEdit={sessionToEdit}
        onEditSession={handleSaveEditedSession}
      />

      {/* Diálogo para eliminar sesión */}
      <Dialog
        open={openConfirmDeleteSession}
        onClose={() => setOpenConfirmDeleteSession(false)}
      >
        <DialogTitle>Eliminar Sesión</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar la sesión "{sessionToDelete?.name}"?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDeleteSession(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeleteSession}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Botón para descargar horarios si hay sesiones */}
      {sessions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <SchedulesPDF competencia={competencia} sessions={sessions} />
        </Box>
      )}

      {/* Listado de sesiones */}
      {sessions.map((session) => (
        <Accordion key={session.id} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>
              Sesión {session.number}: {session.name} - {session.date} {session.startTime}
            </Typography>
            <Box sx={{ ml: 2 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation(); // Evitar que se abra/cierre el accordion
                  handleEditSession(session);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSession(session);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <SessionSchedules
              session={session}
              competencia={competencia}
              onUpdateSession={handleUpdateSession}
              allUsedIds={allUsedIds}
            />
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

export default SessionManager;
